import React from 'react';
import PropTypes from 'prop-types';
import { Browser } from '@syncfusion/ej2-base';
import classNames from 'classnames';
import { colors, fonts } from 'shared/defaultTheme';
import styled from 'styled-components';
import { UncontrolledTooltip } from 'reactstrap';

const primary = 'primary';
const secondary = 'secondary';

export const Button = ({
  label,
  className,
  color,
  full,
  onClick,
  type,
  icon,
  backgroundColor,
  textColor,
  disabled,
}) => (
  <button
    className={classNames(
      {
        button: true,
        nav__text: true,
        'button--primary': color === primary,
        'button--secondary': color === secondary,
        'button--full': full,
        'button--disabled': disabled,
      },
      className
    )}
    style={backgroundColor ? { backgroundColor } : undefined}
    type={type === 'button' ? 'button' : 'submit'}
    onClick={onClick}
    disabled={disabled}
  >
    {icon && <div className="button__icon-wrapper">{icon}</div>}
    <span style={textColor ? { color: textColor } : undefined}>{label}</span>
  </button>
);

Button.defaultProps = {
  className: '',
  color: primary,
  full: false,
  onClick: () => null,
  type: 'button',
  icon: undefined,
  backgroundColor: undefined,
  textColor: undefined,
  disabled: false,
};

Button.propTypes = {
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  color: PropTypes.string,
  full: PropTypes.bool,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit']),
  icon: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  disabled: PropTypes.bool,
};

export const ButtonIcon = ({
  Icon,
  className,
  onClick,
  type,
  id,
  isDisabled,
  title,
  ariaLabel,
}) => (
  <BtnIcon
    className={classNames(className)}
    type={type === 'button' ? 'button' : 'submit'}
    onClick={onClick}
    id={id}
    disabled={isDisabled}
    aria-label={ariaLabel || title}
  >
    <Icon />
    {!Browser.isDevice && (
      <>
        {id && title && (
          <UncontrolledTooltip flip target={id}>
            {title}
          </UncontrolledTooltip>
        )}
      </>
    )}
  </BtnIcon>
);

const BtnIcon = styled.button`
  &:active {
    background-color: ${({ disabled }) => (disabled ? 'transparent' : colors.gullGrey)} !important;
  }
  svg {
    fill: ${({ disabled }) => (disabled ? colors.pattens : colors.suvaGrey)};
    &:hover,
    &:focus {
      fill: ${({ disabled }) => (disabled ? colors.pattens : colors.black)};
    }
  }
`;

ButtonIcon.defaultProps = {
  className: '',
  id: '',
  title: '',
  ariaLabel: '',
  onClick: () => null,
  type: 'button',
  isDisabled: false,
};

ButtonIcon.propTypes = {
  Icon: PropTypes.func.isRequired,
  className: PropTypes.string,
  id: PropTypes.string,
  title: PropTypes.string,
  ariaLabel: PropTypes.string,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit']),
  isDisabled: PropTypes.bool,
};

export const ButtonLabelIcon = ({ Icon, text, className, onClick, type, id, isDisabled }) => (
  <BtnLabelIcon
    className={classNames(className)}
    type={type === 'button' ? 'button' : 'submit'}
    onClick={onClick}
    id={id}
    disabled={isDisabled}
  >
    <Icon />
    <span className="ml-1">{text}</span>
  </BtnLabelIcon>
);

const BtnLabelIcon = styled.button`
  background-color: ${colors.pattens};
  height: 21px;
  width: auto;
  border-radius: 4px;
  display: flex;
  align-items: center;
  fill: ${({ disabled }) => (disabled ? colors.suvaGrey : colors.black)};
  font-weight: 500;
  &:active,
  &:hover {
    background-color: ${({ disabled }) =>
      disabled ? colors.lightSilverTwo : colors.gullGrey} !important;
    font-weight: 600;
  }
  svg {
    height: 14px !important;
    width: 14px !important;
    &:hover,
    &:focus {
      fill: ${({ disabled }) => (disabled ? colors.suvaGrey : colors.black)};
    }
  }
  span {
    font-family: ${fonts.default};
    font-size: 11.5px;
    letter-spacing: 0;
    line-height: 12px;
    text-transform: uppercase;
    padding-top: 4px;
  }
`;

ButtonLabelIcon.defaultProps = {
  className: '',
  id: '',
  onClick: () => null,
  type: 'button',
  isDisabled: false,
};

ButtonLabelIcon.propTypes = {
  Icon: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  className: PropTypes.string,
  id: PropTypes.string,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit']),
  isDisabled: PropTypes.bool,
};

export const ButtonLabelIconOnly = ({ Icon, text, className, onClick, type, id, isDisabled }) => (
  <BtnLabelIconOnly
    className={classNames(className)}
    type={type === 'button' ? 'button' : 'submit'}
    onClick={onClick}
    id={id}
    disabled={isDisabled}
  >
    <span className="ml-1">{text}</span>
    <Icon />
  </BtnLabelIconOnly>
);

const BtnLabelIconOnly = styled.button`
  background-color: transparent;
  color: ${({ disabled }) => (disabled ? colors.gullGrey : colors.black)};
  fill: ${({ disabled }) => (disabled ? colors.gullGrey : colors.black)};
  border: none;
  &:active,
  &:hover,
  &:focus {
    color: ${({ disabled }) => (disabled ? colors.gullGrey : colors.suvaGrey)};
    fill: ${({ disabled }) => (disabled ? colors.gullGrey : colors.suvaGrey)};
  }
  svg {
    height: 25px;
    width: 38.86px;
  }
  span {
    font-family: ${fonts.default};
    font-size: 12px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 15px;
    text-transform: uppercase;
    position: relative;
    top: 3px;
  }
`;

ButtonLabelIconOnly.defaultProps = {
  className: '',
  id: '',
  onClick: () => null,
  type: 'button',
  isDisabled: false,
};

ButtonLabelIconOnly.propTypes = {
  Icon: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  className: PropTypes.string,
  id: PropTypes.string,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit']),
  isDisabled: PropTypes.bool,
};
